import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * ProductCarousel - Carrossel de imagens premium para produtos
 * 
 * @param {Array} images - Array de URLs das imagens
 * @param {string} productTitle - Título do produto para alt text
 * @param {function} onSlideChange - Callback quando o slide muda
 * @param {number} initialSlide - Slide inicial (default: 0)
 * @param {React.ReactNode} overlayContent - Conteúdo overlay (ex: botão de vídeo)
 */
const ProductCarousel = ({ 
  images = [], 
  productTitle = 'Produto',
  onSlideChange,
  initialSlide = 0,
  overlayContent
}) => {
  const [currentSlide, setCurrentSlide] = useState(initialSlide);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [loadedImages, setLoadedImages] = useState(new Set([0]));
  const [showArrows, setShowArrows] = useState(false);
  const containerRef = useRef(null);
  const slideWidth = useRef(0);

  // Reset para slide inicial quando images mudam
  useEffect(() => {
    setCurrentSlide(initialSlide);
    setTranslateX(0);
    // Preload primeira imagem da nova lista
    setLoadedImages(new Set([0, 1]));
  }, [images, initialSlide]);

  // Preload próximas imagens
  useEffect(() => {
    const toLoad = new Set(loadedImages);
    // Carrega atual, anterior e próxima
    toLoad.add(currentSlide);
    if (currentSlide > 0) toLoad.add(currentSlide - 1);
    if (currentSlide < images.length - 1) toLoad.add(currentSlide + 1);
    setLoadedImages(toLoad);
  }, [currentSlide, images.length]);

  // Notifica mudança de slide
  useEffect(() => {
    if (onSlideChange) {
      onSlideChange(currentSlide);
    }
  }, [currentSlide, onSlideChange]);

  // Atualiza largura do container
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        slideWidth.current = containerRef.current.offsetWidth;
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const goToSlide = useCallback((index) => {
    if (isTransitioning || images.length <= 1) return;
    
    let targetIndex = index;
    
    // Loop infinito
    if (index < 0) {
      targetIndex = images.length - 1;
    } else if (index >= images.length) {
      targetIndex = 0;
    }
    
    setIsTransitioning(true);
    setCurrentSlide(targetIndex);
    
    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  }, [images.length, isTransitioning]);

  const goToPrev = useCallback(() => {
    goToSlide(currentSlide - 1);
  }, [currentSlide, goToSlide]);

  const goToNext = useCallback(() => {
    goToSlide(currentSlide + 1);
  }, [currentSlide, goToSlide]);

  // Touch/Mouse handlers para swipe
  const handleDragStart = (clientX) => {
    if (images.length <= 1) return;
    setIsDragging(true);
    setStartX(clientX);
  };

  const handleDragMove = (clientX) => {
    if (!isDragging || images.length <= 1) return;
    const diff = clientX - startX;
    // Limita o arraste para não ir muito além
    const maxDrag = slideWidth.current * 0.5;
    setTranslateX(Math.max(-maxDrag, Math.min(maxDrag, diff)));
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    const threshold = slideWidth.current * 0.15; // 15% da largura para trigger
    
    if (translateX > threshold) {
      goToPrev();
    } else if (translateX < -threshold) {
      goToNext();
    }
    
    setTranslateX(0);
  };

  // Mouse events
  const handleMouseDown = (e) => {
    e.preventDefault();
    handleDragStart(e.clientX);
  };

  const handleMouseMove = (e) => {
    handleDragMove(e.clientX);
  };

  const handleMouseUp = () => {
    handleDragEnd();
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      handleDragEnd();
    }
  };

  // Touch events
  const handleTouchStart = (e) => {
    handleDragStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    handleDragMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    handleDragEnd();
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        goToPrev();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('keydown', handleKeyDown);
      return () => container.removeEventListener('keydown', handleKeyDown);
    }
  }, [goToPrev, goToNext]);

  if (!images || images.length === 0) {
    return (
      <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl aspect-square flex items-center justify-center">
        <div className="text-[#3A3A3D] text-center">
          <div className="h-24 w-24 mx-auto mb-4 bg-[#2A2A2D] rounded-lg" />
          <p className="text-[#666]">Sem imagem disponível</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="relative group"
      onMouseEnter={() => setShowArrows(true)}
      onMouseLeave={() => setShowArrows(false)}
    >
      {/* Main Carousel Container */}
      <div
        ref={containerRef}
        className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl overflow-hidden aspect-square relative select-none"
        tabIndex={0}
        role="region"
        aria-label="Carrossel de imagens do produto"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Slides Container */}
        <div 
          className="flex h-full transition-transform duration-300 ease-out"
          style={{
            transform: `translateX(calc(-${currentSlide * 100}% + ${translateX}px))`,
            cursor: isDragging ? 'grabbing' : (images.length > 1 ? 'grab' : 'default')
          }}
        >
          {images.map((image, index) => (
            <div
              key={`${image}-${index}`}
              className="w-full h-full flex-shrink-0 flex items-center justify-center p-6 md:p-8"
            >
              {loadedImages.has(index) ? (
                <img
                  src={image}
                  alt={`${productTitle} - Imagem ${index + 1}`}
                  className="max-h-full max-w-full object-contain select-none pointer-events-none"
                  draggable={false}
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="animate-pulse bg-[#2A2A2D] w-3/4 h-3/4 rounded-lg" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Overlay Content (ex: botão de vídeo) */}
        {overlayContent && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="pointer-events-auto">
              {overlayContent}
            </div>
          </div>
        )}

        {/* Navigation Arrows - Desktop */}
        {images.length > 1 && (
          <>
            {/* Left Arrow */}
            <button
              onClick={(e) => { e.stopPropagation(); goToPrev(); }}
              className={`
                absolute left-3 top-1/2 -translate-y-1/2 z-10
                w-10 h-10 md:w-12 md:h-12 rounded-full
                bg-black/40 hover:bg-black/70 backdrop-blur-sm
                flex items-center justify-center
                transition-all duration-200
                ${showArrows || isDragging ? 'opacity-100' : 'opacity-0 md:opacity-30 md:hover:opacity-100'}
                focus:outline-none focus:ring-2 focus:ring-[#C9A24D]
              `}
              aria-label="Imagem anterior"
              data-testid="carousel-prev-btn"
            >
              <ChevronLeft className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </button>

            {/* Right Arrow */}
            <button
              onClick={(e) => { e.stopPropagation(); goToNext(); }}
              className={`
                absolute right-3 top-1/2 -translate-y-1/2 z-10
                w-10 h-10 md:w-12 md:h-12 rounded-full
                bg-black/40 hover:bg-black/70 backdrop-blur-sm
                flex items-center justify-center
                transition-all duration-200
                ${showArrows || isDragging ? 'opacity-100' : 'opacity-0 md:opacity-30 md:hover:opacity-100'}
                focus:outline-none focus:ring-2 focus:ring-[#C9A24D]
              `}
              aria-label="Próxima imagem"
              data-testid="carousel-next-btn"
            >
              <ChevronRight className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </button>
          </>
        )}
      </div>

      {/* Dot Indicators */}
      {images.length > 1 && (
        <div 
          className="flex justify-center gap-2 mt-4"
          role="tablist"
          aria-label="Navegação do carrossel"
        >
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`
                w-2.5 h-2.5 rounded-full transition-all duration-200
                focus:outline-none focus:ring-2 focus:ring-[#C9A24D] focus:ring-offset-2 focus:ring-offset-[#0F0F10]
                ${currentSlide === index 
                  ? 'bg-[#C9A24D] w-6' 
                  : 'bg-[#3A3A3D] hover:bg-[#666]'
                }
              `}
              role="tab"
              aria-selected={currentSlide === index}
              aria-label={`Ir para imagem ${index + 1}`}
              data-testid={`carousel-dot-${index}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductCarousel;
